import { useState, useMemo, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  mockJudicialRequirements,
  getJudicialStateLabel,
  getJudicialStateColor,
  getSemaforoColor,
  getPrioridadColor,
} from "@/mocks/judicialRequirements";
import type { JudicialRequirementState, JudicialRequirement } from "@/mocks/judicialRequirements";
import { judicialService } from "@/services/api";
import { mapDbJudicialToFrontend } from "@/services/mapping";


export default function JudicialRequerimientosPage() {
  const navigate = useNavigate();
  const [search, setSearch] = useState("");
  const [estadoFilter, setEstadoFilter] = useState<string>("todos");
  const [prioridadFilter, setPrioridadFilter] = useState<string>("todos");
  const [requirements, setRequirements] = useState<JudicialRequirement[]>([]);
  const [loading, setLoading] = useState(true);
  const [usingMock, setUsingMock] = useState(false);

  const estados: string[] = ["todos", "recibido", "pendiente_asignacion", "solicitando_insumos", "insumos_completos", "borrador_generado", "en_revision", "enviado_tribunal", "vencido", "cerrado"];

  useEffect(() => {
    async function loadData() {
      try {
        setLoading(true);
        const data = await judicialService.list();
        if (Array.isArray(data) && data.length > 0) {
          setRequirements(data.map(mapDbJudicialToFrontend));
          setUsingMock(false);
        } else {
          setRequirements(mockJudicialRequirements);
          setUsingMock(true);
        }
      } catch (err) {
        console.warn("Error loading judicial requirements, using mock fallback:", err);
        setRequirements(mockJudicialRequirements);
        setUsingMock(true);
      } finally {
        setLoading(false);
      }
    }
    loadData();
  }, []);

  const filtered = useMemo(() => {
    let reqs = [...requirements];
    if (estadoFilter !== "todos") reqs = reqs.filter((r) => r.estado === estadoFilter);
    if (prioridadFilter !== "todos") reqs = reqs.filter((r) => r.prioridad === prioridadFilter);
    if (search.trim()) {
      const q = search.toLowerCase();
      reqs = reqs.filter((r) => r.tribunal.toLowerCase().includes(q) || r.rit.toLowerCase().includes(q) || (r.studentName && r.studentName.toLowerCase().includes(q)));
    }
    return reqs;
  }, [estadoFilter, prioridadFilter, search]);

  return (
    <div className="space-y-5">
      {usingMock && (
        <div className="bg-amber-50 border border-amber-200 text-amber-800 text-xs px-4 py-2.5 rounded-xl flex items-center gap-2 animate-fade-in">
          <i className="ri-information-line text-sm flex-shrink-0" />
          <span>Modo Demo: Mostrando requerimientos judiciales simulados del sistema local.</span>
        </div>
      )}

      {loading ? (
        <div className="flex items-center justify-center py-12">
          <span className="flex items-center justify-center w-8 h-8">
            <i className="ri-loader-4-line text-2xl text-foreground-300 animate-spin" />
          </span>
        </div>
      ) : (
        <>
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
            <div>
              <h1 className="text-xl md:text-2xl font-bold text-foreground-900 tracking-tight">Todos los Requerimientos</h1>
              <p className="text-sm text-foreground-500 mt-1">{filtered.length} requerimientos encontrados</p>
            </div>
            <button onClick={() => navigate("/judicial/nuevo")} className="flex items-center gap-2 px-4 py-2.5 bg-primary-500 text-white rounded-xl text-sm font-semibold hover:bg-primary-600 transition-all duration-200 cursor-pointer whitespace-nowrap shadow-soft">
              <span className="w-4 h-4 flex items-center justify-center"><i className="ri-add-line" /></span>
              Nuevo Requerimiento
            </button>
          </div>

          {/* Filters */}
          <div className="flex flex-col sm:flex-row gap-3">
            <div className="relative flex-1 max-w-sm">
              <span className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 flex items-center justify-center text-foreground-400">
                <i className="ri-search-line text-sm" />
              </span>
              <input type="text" value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Buscar..." className="w-full pl-9 pr-3 py-2 bg-background-50 border border-background-200/70 rounded-xl text-sm outline-none focus:border-primary-300" />
            </div>
            <select value={estadoFilter} onChange={(e) => setEstadoFilter(e.target.value)} className="px-3 py-2 bg-background-50 border border-background-200/70 rounded-xl text-sm text-foreground-700 outline-none cursor-pointer">
              {estados.map((e) => (
                <option key={e} value={e}>{e === "todos" ? "Todos los estados" : getJudicialStateLabel(e as JudicialRequirementState)}</option>
              ))}
            </select>
            <select value={prioridadFilter} onChange={(e) => setPrioridadFilter(e.target.value)} className="px-3 py-2 bg-background-50 border border-background-200/70 rounded-xl text-sm text-foreground-700 outline-none cursor-pointer">
              <option value="todos">Todas las prioridades</option>
              <option value="critica">Crítica</option>
              <option value="alta">Alta</option>
              <option value="media">Media</option>
              <option value="baja">Baja</option>
            </select>
          </div>

          {/* Table */}
          <div className="bg-white border border-background-200/70 rounded-xl overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-background-200/70 bg-background-50/50">
                    <th className="text-left px-4 py-3 text-[11px] font-bold text-foreground-400 uppercase tracking-wider">Semáforo</th>
                    <th className="text-left px-4 py-3 text-[11px] font-bold text-foreground-400 uppercase tracking-wider">Tribunal / RIT</th>
                    <th className="text-left px-4 py-3 text-[11px] font-bold text-foreground-400 uppercase tracking-wider">Estudiante</th>
                    <th className="text-left px-4 py-3 text-[11px] font-bold text-foreground-400 uppercase tracking-wider">Tipo</th>
                    <th className="text-left px-4 py-3 text-[11px] font-bold text-foreground-400 uppercase tracking-wider">Estado</th>
                    <th className="text-left px-4 py-3 text-[11px] font-bold text-foreground-400 uppercase tracking-wider">Prioridad</th>
                    <th className="text-left px-4 py-3 text-[11px] font-bold text-foreground-400 uppercase tracking-wider">Plazo</th>
                    <th className="text-left px-4 py-3 text-[11px] font-bold text-foreground-400 uppercase tracking-wider">Responsable</th>
                  </tr>
                </thead>
                <tbody>
                  {filtered.map((req) => {
                    const hoy = new Date();
                    const limite = new Date(req.fechaLimite);
                    const dias = Math.ceil((limite.getTime() - hoy.getTime()) / (1000 * 60 * 60 * 24));
                    return (
                      <tr key={req.id} onClick={() => navigate(`/judicial/requerimiento/${req.id}`)} className="border-b border-background-100/70 hover:bg-background-50/50 cursor-pointer transition-colors">
                        <td className="px-4 py-3">
                          <div className={`w-3 h-3 rounded-full ${getSemaforoColor(req.semaforo)}`} />
                        </td>
                        <td className="px-4 py-3">
                          <p className="font-medium text-foreground-900 text-xs whitespace-nowrap">{req.tribunal}</p>
                          <p className="text-[11px] text-foreground-500">RIT {req.rit}</p>
                        </td>
                        <td className="px-4 py-3 text-xs text-foreground-700 whitespace-nowrap">{req.studentName || "—"}</td>
                        <td className="px-4 py-3 text-xs text-foreground-600 max-w-[180px] truncate">{req.tipoRequerimiento}</td>
                        <td className="px-4 py-3">
                          <span className={`text-[11px] px-2 py-0.5 rounded-full font-medium border ${getJudicialStateColor(req.estado)}`}>
                            {getJudicialStateLabel(req.estado)}
                          </span>
                        </td>
                        <td className="px-4 py-3">
                          <span className={`text-[11px] px-2 py-0.5 rounded-full font-medium border ${getPrioridadColor(req.prioridad)}`}>
                            {req.prioridad === "critica" ? "Crítica" : req.prioridad === "alta" ? "Alta" : req.prioridad === "media" ? "Media" : "Baja"}
                          </span>
                        </td>
                        <td className="px-4 py-3">
                          <span className={`text-xs font-medium ${dias < 0 ? "text-red-600" : dias <= 3 ? "text-amber-600" : "text-foreground-600"}`}>
                            {dias < 0 ? `+${Math.abs(dias)}d` : dias === 0 ? "HOY" : `${dias}d`}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-xs text-foreground-600 whitespace-nowrap">{req.responsableName || <span className="text-red-400">Sin asignar</span>}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}
    </div>
  );
}